export type SrgbColor = readonly [red: number, green: number, blue: number]

const BLACK: SrgbColor = [0, 0, 0]
const WHITE: SrgbColor = [255, 255, 255]

function linearizeSrgbChannel(channel: number) {
  const normalized = channel / 255

  return normalized <= 0.04045
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4
}

export function relativeLuminance([red, green, blue]: SrgbColor) {
  return (
    0.2126 * linearizeSrgbChannel(red) +
    0.7152 * linearizeSrgbChannel(green) +
    0.0722 * linearizeSrgbChannel(blue)
  )
}

export function contrastRatio(first: SrgbColor, second: SrgbColor) {
  const lighter = Math.max(relativeLuminance(first), relativeLuminance(second))
  const darker = Math.min(relativeLuminance(first), relativeLuminance(second))

  return (lighter + 0.05) / (darker + 0.05)
}

export function hslToSrgb(
  hue: number,
  saturation: number,
  lightness: number,
): SrgbColor {
  const normalizedHue = ((hue % 360) + 360) % 360
  const normalizedSaturation = saturation / 100
  const normalizedLightness = lightness / 100
  const chroma =
    (1 - Math.abs(2 * normalizedLightness - 1)) * normalizedSaturation
  const hueSection = normalizedHue / 60
  const secondary = chroma * (1 - Math.abs((hueSection % 2) - 1))
  const offset = normalizedLightness - chroma / 2

  let channels: SrgbColor

  if (hueSection < 1) {
    channels = [chroma, secondary, 0]
  } else if (hueSection < 2) {
    channels = [secondary, chroma, 0]
  } else if (hueSection < 3) {
    channels = [0, chroma, secondary]
  } else if (hueSection < 4) {
    channels = [0, secondary, chroma]
  } else if (hueSection < 5) {
    channels = [secondary, 0, chroma]
  } else {
    channels = [chroma, 0, secondary]
  }

  return [
    Math.round((channels[0] + offset) * 255),
    Math.round((channels[1] + offset) * 255),
    Math.round((channels[2] + offset) * 255),
  ]
}

export function contrastingForegroundForHsl(
  hue: number,
  saturation: number,
  lightness: number,
) {
  const background = hslToSrgb(hue, saturation, lightness)

  return contrastRatio(background, WHITE) > contrastRatio(background, BLACK)
    ? 'hsl(0 0% 100%)'
    : 'hsl(0 0% 0%)'
}
